// framework/client/components/LanguageSelector.js
import { ComponentBase } from "../core/ComponentBase.js";

export class LanguageSelector extends ComponentBase {
    static styles = `
    .language-selector-button {
        cursor: pointer;
        background-color: var(--bs-btn-bg);
        color: var(--bs-btn-color);
        border: 1px solid var(--bs-btn-border-color);
    }
    .language-selector-button:hover {
        background-color: var(--bs-btn-hover-bg);
        color: var(--bs-btn-hover-color);
    }

    .language-selector-menu {
        min-width: 12rem;
        max-height: 200px;
        overflow-y: auto;
        overflow-x: hidden;
        position: absolute;
        z-index: var(--ab-z-dropdown);
        background-color: var(--bs-dropdown-bg);
        color: var(--bs-dropdown-color);
    }
    
    .dropdown-item.active, .dropdown-item:active {
        background-color: var(--bs-primary) !important;
        color: #fff !important;
    }
    
    /* Submenu positioning - appears to the left side of parent menu in LTR */
    .dropend {
        position: relative;
    }
    
    .dropend > .dropdown-menu {
        position: absolute;
        top: 0;
        left: auto;
        right: 100%;
        margin-top: 0;
        margin-right: 0.125rem;
        min-width: 10rem;
    }
    
    /* RTL: submenu appears to the right side */
    [dir="rtl"] .dropend > .dropdown-menu {
        left: 100%;
        right: auto;
        margin-left: 0.125rem;
        margin-right: 0;
    }
    
    /* If parent menu is right-aligned (dropdown-menu-end), submenu should appear on left */
    .dropdown-menu-end .dropend > .dropdown-menu {
        left: auto;
        right: 100%;
        margin-right: 0.125rem;
        margin-left: 0;
    }
    
    /* RTL: right-aligned parent menu */
    [dir="rtl"] .dropdown-menu-end .dropend > .dropdown-menu {
        left: 100%;
        right: auto;
        margin-left: 0.125rem;
        margin-right: 0;
    }
    
    /* Submenu styling - ensure it has proper dropdown appearance */
    .dropend > .dropdown-menu {
        background-color: var(--bs-dropdown-bg);
        border: var(--bs-dropdown-border-width) solid var(--bs-dropdown-border-color);
        border-radius: var(--bs-dropdown-border-radius);
        box-shadow: var(--bs-dropdown-box-shadow);
        padding: var(--bs-dropdown-padding-y) 0;
    }
    
    /* Submenu item styling */
    .dropend .dropdown-menu .dropdown-item {
        padding: 0.5rem 1rem;
        cursor: pointer;
        color: var(--bs-dropdown-link-color);
        white-space: nowrap;
    }
    
    .dropend .dropdown-menu .dropdown-item:hover {
        background-color: var(--bs-dropdown-link-hover-bg);
        color: var(--bs-dropdown-link-hover-color);
    }
    
    .dropend .dropdown-menu li {
        list-style: none;
    }
    
    /* Visual indicator for submenu parent - LTR: left arrow */
    .dropend > .dropdown-toggle::after {
        display: inline-block;
        margin-left: auto;
        vertical-align: 0.255em;
        content: "";
        border-top: 0.3em solid transparent;
        border-right: 0.3em solid;
        border-bottom: 0.3em solid transparent;
        border-left: 0;
    }
    
    /* RTL: right arrow */
    [dir="rtl"] .dropend > .dropdown-toggle::after {
        margin-left: 0;
        margin-right: auto;
        border-right: 0;
        border-left: 0.3em solid;
    }
    
    /* Position the arrow on the right side in LTR, left side in RTL */
    .dropend > .dropdown-toggle {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    
    [dir="rtl"] .dropend > .dropdown-toggle {
        flex-direction: row-reverse;
    }

    /* --- START MOBILE RESPONSIVENESS FIX --- */
    @media (max-width: 767.98px) {
        .dropend > .dropdown-menu,
        .dropstart > .dropdown-menu {
            position: static;
            width: 100%;
            border: none;
            box-shadow: none;
            background-color: transparent;
        }

        .dropend > .dropdown-toggle::after,
        .dropstart > .dropdown-toggle::after {
            display: none; /* Hide the side arrow on mobile */
        }
    }
    /* --- END MOBILE RESPONSIVENESS FIX --- */
    `;

    constructor(config) {
        super(config);
        this.languages = []; // Will be populated by TranslationManager
        this.currentLanguage = null; // Will be managed by TranslationManager
        this.rootContainer = null;
        this.dropdownInstance = null;
        this._boundRender = this.render.bind(this); // For event listener cleanup
    }

    static getDefaultConfig() {
        return {
            renderAs: 'button', // 'button' or 'submenuItem'
        };
    }

    async initialize() {
        this.currentLanguage = this.appClient.tx.getCurrentLanguage();
        await super.initialize();
        await this.render();
    }

    async render() {
        this.languages = this.appClient.tx.getAvailableLanguages() || [];
        if (this.languages.length <= 1 && this.config.renderAs === 'submenuItem') {
            if (!this.rootContainer) {
                this.rootContainer = document.createElement('div');
                this.rootContainer.style.display = 'none';
            }
            return this.rootContainer;
        }
        
        if (this.config.renderAs === 'submenuItem') {
            return this._renderAsSubmenuItem();
        }
        return this._renderAsButton();
    }

    _renderAsButton() {
        if (!this.rootContainer || !this.rootContainer.classList.contains('language-selector-container')) {
            // Re-create if it was previously a submenu item or not initialized
            this.rootContainer = document.createElement('div');
            this.rootContainer.classList.add('language-selector-container', 'dropdown');

            this.languageButton = document.createElement('button');
            this.languageButton.classList.add('btn', 'dropdown-toggle', 'language-selector-button');
            this.languageButton.setAttribute('title', 'Select Language');
            this.languageButton.setAttribute('data-bs-toggle', 'dropdown');
            this.languageButton.setAttribute('aria-expanded', 'false');
            this.languageButton.innerHTML = '<i class="bi bi-translate"></i>';
            this.rootContainer.appendChild(this.languageButton);

            this.languageMenu = document.createElement('div');
            this.languageMenu.classList.add('dropdown-menu', 'language-selector-menu');
            this.rootContainer.appendChild(this.languageMenu);

            this.dropdownInstance = new bootstrap.Dropdown(this.languageButton);

            // --- START FIX: Refresh languages before showing dropdown ---
            this.languageButton.addEventListener('show.bs.dropdown', () => {
                this.languages = this.appClient.tx.getAvailableLanguages() || [];
                this.currentLanguage = this.appClient.tx.getCurrentLanguage();
                this.updateLanguageMenu();
            });
            // --- END FIX ---
        }

        this.updateLanguageMenu();
        this.appClient.tx.applyTranslations(this.rootContainer);
        return this.rootContainer;
    }

    _renderAsSubmenuItem() {
        if (!this.rootContainer || this.rootContainer.tagName !== 'LI') {
            this.rootContainer = document.createElement('li');
            
            // --- START LTR/RTL FIX ---
            const direction = document.documentElement.getAttribute('dir') || 'ltr';
            this.rootContainer.classList.add(direction === 'rtl' ? 'dropstart' : 'dropend');
            // --- END LTR/RTL FIX ---

            const submenuToggle = document.createElement('a');
            submenuToggle.href = '#';
            submenuToggle.classList.add('dropdown-item', 'dropdown-toggle');
            // Don't use data-bs-toggle as it conflicts with manual control
            submenuToggle.innerHTML = `<i class="bi bi-translate"></i><span data-i18n="Language">Language</span>`;
            this.rootContainer.appendChild(submenuToggle);

            this.languageMenu = document.createElement('ul');
            this.languageMenu.classList.add('dropdown-menu');
            this.rootContainer.appendChild(this.languageMenu);

            // Manual submenu toggle on hover/click
            submenuToggle.addEventListener('mouseenter', () => {
                this.languageMenu.classList.add('show');
                // Update menu when showing
                this.languages = this.appClient.tx.getAvailableLanguages() || [];
                this.currentLanguage = this.appClient.tx.getCurrentLanguage();
                this.updateLanguageMenu();
            });

            this.rootContainer.addEventListener('mouseleave', () => {
                this.languageMenu.classList.remove('show');
            });

            // Also support click to toggle
            submenuToggle.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const isShown = this.languageMenu.classList.contains('show');
                this.languageMenu.classList.toggle('show');
                // Update menu when showing
                if (!isShown) {
                    this.languages = this.appClient.tx.getAvailableLanguages() || [];
                    this.currentLanguage = this.appClient.tx.getCurrentLanguage();
                    this.updateLanguageMenu();
                }
            });

            // CRITICAL FIX: Do NOT initialize a new dropdown here.
            // The parent dropdown will manage this as a submenu automatically.
        }

        this.updateLanguageMenu();
        this.appClient.tx.applyTranslations(this.rootContainer);
        return this.rootContainer;
    }

    updateLanguageMenu() {
        // Always get the latest languages when updating
        this.languages = this.appClient.tx.getAvailableLanguages() || [];
        this.currentLanguage = this.appClient.tx.getCurrentLanguage();
        
        this.languageMenu.innerHTML = '';

        this.languages.forEach(language => {
            this.addMenuItem(language);
        });
    }

    addMenuItem(language) {
        const isSubMenu = this.config.renderAs === 'submenuItem';

        if (isSubMenu) {
            // For submenu: create <li> with nested <a class="dropdown-item">
            const li = document.createElement('li');
            
            const link = document.createElement('a');
            link.href = '#';
            link.classList.add('dropdown-item');
            link.textContent = language.name;
            link.dataset.languageCode = language.code;
            
            if (this.currentLanguage === language.code) {
                link.classList.add('active');
            }
            
            link.addEventListener('click', async (event) => {
                event.preventDefault();
                event.stopPropagation();
                await this.appClient.tx.setLanguage(language.code);
                this.currentLanguage = this.appClient.tx.getCurrentLanguage();
                
                // Hide the submenu
                this.languageMenu.classList.remove('show');
                
                // Manually hide the main dropdown menu after selection
                const parentDropdown = this.rootContainer.closest('.dropdown-menu.show');
                if (parentDropdown) {
                    const dropdownToggle = parentDropdown.previousElementSibling;
                    const dropdownInstance = bootstrap.Dropdown.getInstance(dropdownToggle);
                    if (dropdownInstance) {
                        dropdownInstance.hide();
                    }
                }
                this.updateLanguageMenu();
            });
            
            li.appendChild(link);
            this.languageMenu.appendChild(li);
        } else {
            // For button mode: create <a class="dropdown-item"> directly
            const link = document.createElement('a');
            link.href = '#';
            link.classList.add('dropdown-item');
            link.textContent = language.name;
            link.dataset.languageCode = language.code;
            
            if (this.currentLanguage === language.code) {
                link.classList.add('active');
            }
            
            link.addEventListener('click', async (event) => {
                event.preventDefault();
                event.stopPropagation();
                await this.appClient.tx.setLanguage(language.code);
                this.currentLanguage = this.appClient.tx.getCurrentLanguage();
                
                // Manually hide the main dropdown menu after selection
                const parentDropdown = this.rootContainer.closest('.dropdown-menu.show');
                if (parentDropdown) {
                    const dropdownInstance = bootstrap.Dropdown.getInstance(parentDropdown.previousElementSibling);
                    if (dropdownInstance) {
                        dropdownInstance.hide();
                    }
                }
                this.updateLanguageMenu();
            });
            
            this.languageMenu.appendChild(link);
        }
    }

    destroy() {
        this.appClient.events.local.off('languagesInitialized', this._boundRender);
        super.destroy();
        if (this.dropdownInstance) {
            this.dropdownInstance.dispose();
            this.dropdownInstance = null;
        }
        if (this.rootContainer) this.rootContainer.remove();
    }
}